import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';

import { IValidatorMetadata } from 'app/extensibility/metadatas/validator/validator-metadata';
import { azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { format } from 'app/shared/utils/string.util';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { PrivateEndpointType } from 'shell-models-lib';
import { ISelectMetadata } from 'app/extensibility/metadatas/control/select-metadata';
import { PrimaryEndpoints } from 'app/extensibility/metadatas/control/azure-storage-account-select-metadata';

const storageResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.Storage/storageAccounts/<resourceName>';
export const storageResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft.Storage\/storageAccounts\/.+';
export const storageResourceIdErrorMessage = format(stringResources.InvalidResourceId, storageResourceId);

const sqlResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.Sql/servers/<resourceName>';
export const sqlResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft\.Sql\/servers\/.+';
export const sqlResourceIdErrorMessage = format(stringResources.InvalidResourceId, sqlResourceId);

const synapseResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.Synapse/workspaces/<resourceName>';
export const synapseResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft\.(Synapse\/workspaces|Sql\/servers)\/.+';
export const synapseResourceIdErrorMessage = format(stringResources.InvalidResourceId, synapseResourceId);

const sqlMIResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.Sql/managedInstances/<resourceName>';
export const sqlMIResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft\.Sql\/managedInstances\/.+';
export const sqlMIResourceIdErrorMessage = format(stringResources.InvalidResourceId, sqlMIResourceId);

const cosmosResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.DocumentDb/databaseAccounts/<resourceName>';
export const cosmosResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft.DocumentDb\/databaseAccounts\/.+';
export const cosmosResourceIdErrorMessage = format(stringResources.InvalidResourceId, cosmosResourceId);

const privateLinkResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.Network/privateLinkServices/<resourceName>';
export const privateLinkResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft.Network\/privateLinkServices\/.+';
export const privateLinkResourceIdErrorMessage = format(stringResources.InvalidResourceId, privateLinkResourceId);

const azureDataExplorerResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.Kusto/clusters/<resourceName>';
export const azureDataExplorerResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft.Kusto\/clusters\/.+';
export const azureDataExplorerResourceIdErrorMessage = format(stringResources.InvalidResourceId, azureDataExplorerResourceId);

const azureFunctionResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.Web/sites/<resourceName>';
export const azureFunctionResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft.Web\/sites\/.+';
export const azureFunctionResourceIdErrorMessage = format(stringResources.InvalidResourceId, azureFunctionResourceId);

const azureDatabricksResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.Databricks/workspaces/<resourceName>';
export const azureDatabricksResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft.Databricks\/workspaces\/.+';
export const azureDatabricksResourceIdErrorMessage = format(stringResources.InvalidResourceId, azureDatabricksResourceId);

const babylonResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.Purview/accounts/<resourceName>';
export const babylonResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft.Purview\/accounts\/[-A-Za-z0-9]+$';
export const babylonResourceIdErrorMessage = format(stringResources.InvalidResourceId, babylonResourceId);

const mlResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.MachineLearningServices/workspaces/<resourceName>';
export const mlResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft.MachineLearningServices\/workspaces\/.+';
export const mlResourceIdErrorMessage = format(stringResources.InvalidResourceId, mlResourceId);

const cognitiveServicesResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.CognitiveServices/accounts/<resourceName>';
export const cognitiveServicesResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft.CognitiveServices\/accounts\/.+';
export const cognitiveServicesResourceIdErrorMessage = format(stringResources.InvalidResourceId, cognitiveServicesResourceId);

export const eventHubResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.EventHub/namespaces/<resourceName>';
export const eventHubResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft.EventHub\/namespaces\/.+';
export const eventHubResourceIdErrorMessage = format(stringResources.InvalidResourceId, eventHubResourceId);

export const azureMonitorPrivateLinkScopeResourceId = '/subscriptions/<subscriptionID>/resourceGroups/<resourceGroupName>/providers/Microsoft.Insights/privateLinkScopes/<resourceName>';
export const azureMonitorPrivateLinkScopeResourceIdFormat = '\/?subscriptions\/.+\/resourceGroups\/.+\/providers\/Microsoft.Insights\/privateLinkScopes\/.+';
export const azureMonitorPrivateLinkScopeResourceIdErrorMessage = format(stringResources.InvalidResourceId, azureMonitorPrivateLinkScopeResourceId);

export function getManualResourceIdControl(placeholder: string, formatValidator?: string, formatErrorMessage?: string, readonly?: boolean): ControlMetadata {
    const validatorList: IValidatorMetadata[] = [{
        type: 'required',
        errorMessage: ''
    }];

    if (formatValidator) {
        validatorList.push({
            type: 'pattern',
            pattern: formatValidator,
            patternFlags: 'i', // case insensitive regex
            errorMessage: formatErrorMessage || ''
        });
    }

    return {
        type: 'textarea',
        bind: 'privateLinkResourceId',
        label: stringResources.TargetResourceIDLabel,
        required: true,
        isLabelVertical: true,
        validators: validatorList,
        description: stringResources.PrivateEndpointTargetResourceIdDescription,
        readonly: !!readonly,
        placeholder
    };
}

export function getStorageAccountControls(isAdlsGen2: boolean, serviceType: PrimaryEndpoints): ControlMetadata[] {
    return [{
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AccountSelectionMethodLabel,
        description: stringResources.StorageAccountSelection,
        options: azureResourceSelectMethodOptions,
        isLabelVertical: true,
        defaultValue: '{privateLinkResourceId != null ? "manual" : "azure"}',
        cases: [
            {
                caseValue: 'azure',
                children: [
                    {
                        serviceType,
                        type: 'azure-storage-account-select',
                        fetchAccountKey: false,
                        v2Only: true,
                        blobfsOnly: isAdlsGen2,
                        bind: {
                            accountName: '',
                            scope: 'privateLinkResourceId'
                        },
                        validators: {
                            accountName: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    }
                ]
            },
            {
                caseValue: 'manual',
                children: [
                    getManualResourceIdControl(
                        format(stringResources.PrivateEndpointResourceIdPlaceholder, storageResourceId),
                        storageResourceIdFormat,
                        storageResourceIdErrorMessage)
                ]
            }
        ]
    }];
}

export function getPrivateLinkControls(): ControlMetadata[] {
    return [{
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AccountSelectionMethodLabel,
        description: stringResources.StorageAccountSelection,
        options: azureResourceSelectMethodOptions,
        isLabelVertical: true,
        defaultValue: '{privateLinkResourceId != null ? "manual" : "azure"}',
        cases: [
            {
                caseValue: 'azure',
                children: [
                    {
                        type: 'azure-private-link-select',
                        bind: {
                            privateLinkName: '',
                            privateLinkResourceId: 'privateLinkResourceId'
                        },
                        resourceType: 'Microsoft.Network/privateLinkServices',
                        resourceNameText: stringResources.PrivateLink,
                        validators: {
                            privateLinkResourceId: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    }
                ]
            },
            {
                caseValue: 'manual',
                children: [
                    getManualResourceIdControl(
                        format(stringResources.PrivateEndpointResourceIdPlaceholder, privateLinkResourceId),
                        privateLinkResourceIdFormat,
                        privateLinkResourceIdErrorMessage)
                ]
            }
        ]
    }];
}

export function getAzureDataExplorerControls(): ControlMetadata[] {
    return [{
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AzureDataExplorer_SelectionMethod,
        description: stringResources.AzureDataExplorer_SelectMethodDescription,
        options: azureResourceSelectMethodOptions,
        isLabelVertical: true,
        defaultValue: '{privateLinkResourceId != null ? "manual" : "azure"}',
        cases: [
            {
                caseValue: 'azure',
                children: [
                    {
                        type: 'azure-data-explorer-select',
                        bind: {
                            subscriptionId: '',
                            resourceGroupName: '',
                            clusterName: '',
                            resourceId: 'privateLinkResourceId'
                        },
                        validators: {
                            resourceId: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    }
                ]
            },
            {
                caseValue: 'manual',
                children: [
                    getManualResourceIdControl(
                        format(stringResources.PrivateEndpointResourceIdPlaceholder, azureDataExplorerResourceId),
                        azureDataExplorerResourceIdFormat,
                        azureDataExplorerResourceIdErrorMessage)
                ]
            }
        ]
    }];
}

export function getAzureFunctionControls(): ControlMetadata[] {
    return [{
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AzureFunction_SelectionMethod,
        description: stringResources.AzureFunction_SelectMethodDescription,
        options: azureResourceSelectMethodOptions,
        isLabelVertical: true,
        defaultValue: '{privateLinkResourceId != null ? "manual" : "azure"}',
        cases: [
            {
                caseValue: 'azure',
                children: [
                    {
                        type: 'azure-function-select',
                        bind: {
                            functionAppUrl: '',
                            resourceId: 'privateLinkResourceId'
                        },
                        validators: {
                            resourceId: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    }
                ]
            },
            {
                caseValue: 'manual',
                children: [
                    getManualResourceIdControl(
                        format(stringResources.PrivateEndpointResourceIdPlaceholder, azureFunctionResourceId),
                        azureFunctionResourceIdFormat,
                        azureFunctionResourceIdErrorMessage)
                ]
            }
        ]
    }];
}

export function getDatabricksControls(): ControlMetadata[] {
    return [{
        type: 'radio-switch',
        nested: 1,
        label: stringResources.Databricks_SelectionMethod,
        description: stringResources.Databricks_SelectMethodDescription,
        options: azureResourceSelectMethodOptions,
        isLabelVertical: true,
        defaultValue: '{privateLinkResourceId != null ? "manual" : "azure"}',
        cases: [
            {
            caseValue: 'azure',
                children: [
                    {
                        type: 'azure-databricks-workspace-select',
                        bind: 'privateLinkResourceId',
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ]
                    }
                ]
            },
            {
                caseValue: 'manual',
                children: [
                    getManualResourceIdControl(
                        format(stringResources.PrivateEndpointResourceIdPlaceholder, azureDatabricksResourceId),
                        azureDatabricksResourceIdFormat,
                        azureDatabricksResourceIdErrorMessage)
                ]
            }
        ]
    }];
}

export function getSynapseControls(): ControlMetadata[] {
    return [{
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AzureSynapseArtifacts_Workspace_SelectionMethod,
        description: stringResources.AzureSynapseArtifacts_Workspace_SelectMethodDescription,
        options: azureResourceSelectMethodOptions,
        isLabelVertical: true,
        defaultValue: '{privateLinkResourceId != null ? "manual" : "azure"}',
        cases: [
            {
            caseValue: 'azure',
                children: [
                    {
                        type: 'azure-synapse-workspace-select',
                        bind: {
                            endpoint: '',
                            workspaceResourceId: 'privateLinkResourceId'
                        },
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ]
                    }
                ]
            },
            {
                caseValue: 'manual',
                children: [
                    getManualResourceIdControl(
                        format(stringResources.PrivateEndpointResourceIdPlaceholder, synapseResourceId),
                        synapseResourceIdFormat,
                        synapseResourceIdErrorMessage)
                ]
            }
        ]
    }];
}

export function getSqlControls(isSqlDW: boolean, isSqlMI: boolean = false, groupIdList?: PrivateEndpointType[]): ControlMetadata[] {
    const children = [
        getManualResourceIdControl(
            format(stringResources.PrivateEndpointResourceIdPlaceholder, isSqlMI
                ? sqlMIResourceId
                : isSqlDW ? synapseResourceId : sqlResourceId),
            isSqlMI
                ? sqlMIResourceIdFormat
                : isSqlDW ? synapseResourceIdFormat : sqlResourceIdFormat,
            isSqlMI
                ? sqlMIResourceIdErrorMessage
                : isSqlDW ? synapseResourceIdErrorMessage : sqlResourceIdErrorMessage)
    ];

    let groupIdControls: ISelectMetadata;
    if (isSqlDW) {
        if (groupIdList) {
            groupIdControls = getGroupIdDropdownControls('synapsePeSelection', groupIdList);
            children.push(groupIdControls);
        } else {
            children.push({
                type: 'radio',
                bind: 'groupId',
                label: stringResources.EndpointType,
                defaultValue: 'sql',
                options: [
                    {
                        label: stringResources.Dedicated,
                        value: 'sql'
                    },
                    {
                        label: stringResources.Serverless,
                        value: 'sqlOnDemand'
                    }
                ],
                required: true,
                isLabelVertical: true
            });
        }
    }

    return [{
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AccountSelectionMethodLabel,
        description: stringResources.AzureSql_SelectMethodDescription,
        options: azureResourceSelectMethodOptions,
        defaultValue: '{privateLinkResourceId != null ? "manual" : "azure"}',
        isLabelVertical: true,
        cases: [
            {
                caseValue: 'azure',
                children: [
                    {
                        type: 'azure-sql-db-select',
                        bind: {
                            serverName: 'serverName',
                            serverResourceId: 'privateLinkResourceId',
                            databaseName: '',
                            fullyQualifiedDomainName: '',
                            groupID: 'groupId'
                        },
                        isSqlDW,
                        isSqlMI,
                        isServerSelectOnly: true,
                        resourceType: isSqlMI ? 'Microsoft.Sql/managedInstances' : 'Microsoft.Sql/servers',
                        supportOnDemand: isSqlDW,
                        groupIdOptions: groupIdControls?.options,
                        synchronizeKey: groupIdControls?.synchronizeKey,
                        validators: {
                            serverName: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    }
                ]
            },
            {
                caseValue: 'manual',
                children
            }
        ]
    }];
}

export function getReadonlyControl(formatValidator?: string, formatErrorMessage?: string): ControlMetadata[] {
    return [
        {
            type: 'textboxwithcopy',
            bind: 'privateLinkResourceId',
            label: stringResources.TargetResourceIDLabel,
            required: true,
            isLabelVertical: true,
            validators: [{
                type: 'pattern',
                pattern: formatValidator,
                patternFlags: 'i',
                errorMessage: formatErrorMessage || ''
            }],
            descriptionPosition: 'left',
            description: stringResources.PrivateEndpointTargetResourceIdDescription,
            readonly: true
        },
        {
            type: 'textboxwithcopy',
            bind: 'groupId',
            readonly: true,
            isLabelVertical: true,
            label: stringResources.TargetSubResource,
            description: stringResources.TargetSubResourceDescription,
            descriptionPosition: 'left'
        },
        {
            type: 'textboxwithcopy',
            bind: 'ipAddress',
            label: stringResources.ManagedPrivateEndpointPrivateIPLabel,
            required: true,
            isLabelVertical: true,
            descriptionPosition: 'left',
            description: stringResources.PrivateEndpointPrivateIPDescription,
            readonly: true
        },
        {
            type: 'textboxwithcopy',
            bind: 'resourceId',
            label: stringResources.ManagedPrivateEndpointResourceIDLabel,
            required: true,
            isLabelVertical: true,
            descriptionPosition: 'left',
            description: stringResources.PrivateEndpointResourceIdDescription,
            readonly: true
        }
    ].filter((x) => x !== null) as ControlMetadata[];
}

export function getCosmosDBControl(
    isCosmosDbMongoDbApi: boolean = false,
    groupIdList?: PrivateEndpointType[]): ControlMetadata[] {

    const groupIdControl = getGroupIdDropdownControls('cosmosDBPeSelection', groupIdList);
    const groupIdControlList = groupIdControl ? [groupIdControl] : [];

    return [{
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AccountSelectionMethodLabel,
        description: stringResources.AzureCosmos_SelectMethodDescription,
        options: azureResourceSelectMethodOptions,
        isLabelVertical: true,
        defaultValue: '{privateLinkResourceId != null ? "manual" : "azure"}',
        cases: [
            {
                caseValue: 'azure',
                children: [
                    {
                        type: 'azure-cosmos-db-select',
                        fetchAccountKey: false,
                        isAccountSelectOnly: true,
                        isCosmosDbMongoDbApi,
                        bind: {
                            resourceID: 'privateLinkResourceId',
                            resourceName: 'resourceName'
                        },
                        validators: {
                            resourceID: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    },
                    ...groupIdControlList
                ]
            },
            {
                caseValue: 'manual',
                children: [
                    getManualResourceIdControl(
                        format(stringResources.PrivateEndpointResourceIdPlaceholder, cosmosResourceId),
                        cosmosResourceIdFormat,
                        cosmosResourceIdErrorMessage),
                    ...groupIdControlList
                ]
            }
        ]
    }];
}

export function getAzureMLControls(): ControlMetadata[] {
    return [{
            type: 'radio-switch',
            nested: 1,
            label: stringResources.AzureMLService_Workspace_SelectionMethod,
            description: stringResources.AzureMLService_Workspace_SelectMethodDescription,
            options: azureResourceSelectMethodOptions,
            isLabelVertical: true,
            defaultValue: '{privateLinkResourceId != null ? "manual" : "azure"}',
            cases: [
                {
                    caseValue: 'azure',
                    children: [
                        {
                            type: 'azure-ml-service-workspace-select',
                            bind: {
                                subscriptionId: '',
                                resourceGroupName: '',
                                mlWorkspaceName: '',
                                resourceId: 'privateLinkResourceId'
                            },
                            validators: {
                                resourceId: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        }
                    ]
                },
                {
                    caseValue: 'manual',
                    children: [
                        getManualResourceIdControl(
                            format(stringResources.PrivateEndpointResourceIdPlaceholder, mlResourceId),
                            mlResourceIdFormat,
                            mlResourceIdErrorMessage)
                    ]
                }
            ]
        }
    ];
}

export function getCognitiveServicesControls(): ControlMetadata[] {
    return [{
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AccountSelectionMethodLabel,
        description: stringResources.StorageAccountSelection,
        options: azureResourceSelectMethodOptions,
        isLabelVertical: true,
        defaultValue: '{privateLinkResourceId != null ? "manual" : "azure"}',
        cases: [
            {
                caseValue: 'azure',
                children: [
                    {
                        type: 'azure-cognitive-services-select',
                        bind: {
                            name: '',
                            resourceId: 'privateLinkResourceId'
                        },
                        validators: {
                            resourceId: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    }
                ]
            },
            {
                caseValue: 'manual',
                children: [
                    getManualResourceIdControl(
                        format(stringResources.PrivateEndpointResourceIdPlaceholder, cognitiveServicesResourceId),
                        cognitiveServicesResourceIdFormat,
                        cognitiveServicesResourceIdErrorMessage)
                ]
            }
        ]
    }];
}

function getGroupIdDropdownControls(key: string, groupIdList?: PrivateEndpointType[]): ISelectMetadata {
    const options: Array<ISelectOption<PrivateEndpointType>> = groupIdList?.map((groupId) => {
            return {
                    label: groupId,
                    value: groupId
                };
            });

    return options
        ? {
            type: 'select',
            bind: 'groupId',
            label: stringResources.TargetSubResource,
            description: stringResources.TargetSubResourceDescription,
            required: true,
            options,
            isLabelVertical: true,
            synchronizeKey: key
        }
        : undefined;
}
